import { type MouseEvent, Suspense, useState } from "react";
import { Link } from "react-router";
import styled, { css } from "styled-components";

import { Bone } from "~/src/modules/ui-kit";

import useDuplicateProduct from "../../../../../clients/products/useDuplicateProduct";
import { productSearch } from "../../../../../modules/api";
import * as UI from "../../../../../modules/ui-kit";
import * as datetime from "../../../modules/datetime";
import ProductContainer from "../../components/entities/product/ProductContainer";


export default function ProductCardInExplorer(props) {
    const { id, onDuplicate } = props;
    return (
        <StyledRow>
            <Suspense fallback={ <ProductSkeleton /> }>
                <ProductContainer
                    as={ Link }
                    Component={ ProductCard }
                    id={ id }
                    to={ `./Product/${ id }` }
                />
            </Suspense>
            <DuplicateButton id={ id } onDuplicate={ onDuplicate } />
        </StyledRow>
    );
}

function DuplicateButton({ id, onDuplicate }: { id: string, onDuplicate?: (id: string) => void }) {
    const [duplicate, mutation] = useDuplicateProduct();

    async function handleDuplicate(e: MouseEvent) {
        e.preventDefault();
        e.stopPropagation();

        const naturalKey = crypto.randomUUID();
        const result = await duplicate({
            variables: {
                selector: { id },
                naturalKey,
            },
        });

        if (result.data?.duplicateProduct.errors?.length) {
            console.error(result.data.duplicateProduct.errors);
            return;
        }

        const newId = result.data?.duplicateProduct.product?.id;
        if (newId) {
            onDuplicate?.(newId.replaceAll("-", ""));
            void productSearch.refetchQueries({ include: "active" });
        }
    }

    return (
        <StyledDuplicateButton onClick={ handleDuplicate } disabled={ mutation.loading } title="Duplicate product">
            { mutation.loading ? "…" : "Duplicate" }
        </StyledDuplicateButton>
    );
}

function ProductCard(props) {
    const { archived, bimClassName, bimClassType, brandName, countries, createdAt, name, updatedAt, ...restProps } = props;
    return (
        <Styled
            $archived={archived}
            as={ props.onClick ? "button" : "div" }
            { ...restProps }
        >
            <span className="brand">{ brandName }</span>
            <span className="name">{ name ?? "[THIS PRODUCT DOES NOT HAVE A NAME]" }</span>
            <span className="bimclass">{ bimClassName }</span>
            <span className="updated">
                { datetime.exact(updatedAt) }
                { archived && <span className="archived-label">[ARCHIVED]</span> }
            </span>
            <span className="countries">
                {
                    countries?.map?.(
                        (country) => <span key={ country }>{ country }</span>
                    )
                }
            </span>
        </Styled>
    );
}

export function ProductSkeleton() {
    const [brandSizeModifier] = useState(5 + 10 * Math.random());
    const [nameSizeModifier] = useState(10 + 20 * Math.random());
    const [bimClassSizeModifier] = useState(5 + 10 * Math.random());
    return (
        <Styled as="div">
            <Bone className="brand" height="1em" width={ brandSizeModifier + "em" } />
            <Bone className="name" height="2em" width={ nameSizeModifier + "em" } />
            <Bone className="bimclass" height="1em" width={ bimClassSizeModifier + "em" } />
            <Bone className="updated" height="1em" width="12em" />
        </Styled>
    );
}

const Styled = styled.button<{ $archived?: boolean }>`
    color: ${ ({ $archived }) => $archived ? "#d00" : "#000" };
    display: grid;
    gap: 2px 32px;
    grid-template-columns: 1fr min-content min-content;
    grid-template-rows: min-content min-content min-content;
    padding: 4px;
    text-decoration: none;
    transition: 200ms;

    .brand {
        color: ${ ({ $archived }) => $archived ? "#d009" : "#0007" };
        font-size: .8em;
        grid-column: 1 / -1;
        white-space: nowrap;
    }

    .name {
        font-size: 1em;
        letter-spacing: 0.05em;
    }

    .bimclass {
        align-self: end;
        font-size: .8em;
        justify-self: end;
        white-space: nowrap;
    }

    .updated {
        align-self: end;
        color: ${ ({ $archived }) => $archived ? "#d007" : "#0005" };
        display: flex;
        font-family: var(--font-mono), monospace;
        font-size: .6em;
        gap: 8px;
        position: relative;
        text-align: right;
        white-space: nowrap;

        .archived-label {
            color: #d00;
            font-family: var(--font-sans), sans-serif;
            font-weight: bold;
        }
    }

    .countries {
        color: #0005;
        font-size: .8em;
        grid-column: 1 / -1;

        &:empty {
            display: none;
        }

        & > span {
            &:not(:first-child)::before {
                content: ", ";
            }
        }
    }

    ${ () => CSSClickable }
`;

const CSSClickable = css`
    cursor: pointer;

    &:hover {
        .name {
            text-decoration: underline;
        }
    }
`;

const StyledRow = styled.div`
    align-items: stretch;
    display: flex;
    gap: 4px;
    position: relative;

    & > a, & > button:first-child {
        flex: 1;
        min-width: 0;
    }
`;

const StyledDuplicateButton = styled(UI.A).attrs({ $size: "large" })`
    align-self: center;

    &:not([href]) {
        cursor: pointer;
        opacity: 1;

        &:hover {
            background: #0001;
            border-color: transparent;
        }

        &:active {
            background: #0002;
            border-color: transparent;
        }
    }

    &:disabled {
        cursor: default;
        opacity: 0.5;
    }
`;
